import {AbstractSearchCore, IFacet, Result} from ".";
import algoliasearch from "algoliasearch";
import {IFacetFilters} from "./IFacetFilters";

export class AlgoliaSearchCore extends AbstractSearchCore {
  pageNumber: number = 0;
  facets:IFacet[] = [];
  query:string = "";
  client: any;
  index: any;
  facetFilters: IFacetFilters = {};

  async loadFacetFilters() {
    const settings = await this.index.getSettings();
    let facets = settings.attributesForFaceting;
    facets = facets.map(
      (facet: string) => facet.replace('searchable(', '').replace(')', '')
    );

    const promises: any[] = [];
    facets.forEach((facet: any) => {
      const promise = this.index.searchForFacetValues(facet, '', { maxFacetHits: 100 });
      promises.push(promise);
    });
    const facetHits = await Promise.all(promises);
    const facetMap: any = {};
    facetHits.forEach((hits, i) => {
      const facet = facets[i];
      facetMap[facet] = hits.facetHits.map((hit: any) => hit.value);
    });

    this.facetFilters = facetMap;
    return facetMap;
  }

  constructor(groupId: string, config?: any) {
    super(groupId, config);
    this.client = algoliasearch(config.appId, config.apiKey);
    this.index = this.client.initIndex(config.indexName);
    this.query = config?.defaultQuery;
    this.notifySearchTextUpdate(config?.defaultQuery);
    this.facets = config?.defaultFacets || [];
  }

  async getResult(): Promise<Result> {
    const currentFacetFilters: string[] = []
    this.facets.forEach(facet => {
      facet.options.forEach(option => {option.selected && currentFacetFilters.push(`${facet.name}:${option.value}`)});
    });
    const convertFacetsToFiltersString = (inputArray: string[]): string => {
      const groupedByKey: { [key: string]: string[] } = {};
      inputArray.forEach(item => {
        const [key, value] = item.split(':');
        if (!groupedByKey[key]) {
          groupedByKey[key] = [];
        }
        groupedByKey[key].push(value);
      });
      const resultParts: string[] = [];
      for (const key in groupedByKey) {
        const values = groupedByKey[key];
        if (values.length === 1) {
          resultParts.push(`${key}:"${values[0]}"`);
        } else {
          const orPart = values.map(value => `${key}:"${value}"`).join(' OR ');
          resultParts.push(`(${orPart})`);
        }
      }
      return resultParts.join(' AND ');
    }
    const filters = convertFacetsToFiltersString(currentFacetFilters);
    const searchResult: any = await this.index.search(this.query, {
      filters: filters,
      page: this.pageNumber || 0
    });
    const result = new Result();
    result.page = searchResult.page;
    result.itemsPerPage = searchResult.hitsPerPage;
    result.total = searchResult.nbHits;
    result.items = searchResult.hits;
    return result;
  }

  public async executeSearch(query: string):Promise<Result> {
    this.query = query;
    this.pageNumber = 0;
    let result:Result = await this.getResult();
    this.notifySubscribers(result);
    return result;
  }

  private notifySubscribers(result: Result) {
    this.subscribers.forEach(subscriber => {
      subscriber.onResultUpdate(result)
    })
  }

  private notifyFacetUpdate(facets: IFacet[]) {
    this.subscribers.forEach(subscriber => {
      subscriber.onFacetUpdate(facets)
    })
  }

  private notifySearchTextUpdate(searchQuery: string) {
    this.subscribers.forEach(subscriber => {
      subscriber.onSearchTextUpdate(searchQuery)
    })
  }

  public async setFacet(facet:IFacet):Promise<Result> {
    const currentFacet = this.facets.find((value: IFacet) => {
      return value.name === facet.name;
    });

    if (!currentFacet) {
      this.facets.push(facet);
    } else {
      const currentFacetOptions = currentFacet.options;
      const optionIndex = currentFacetOptions.findIndex(option => option.value === facet.options[0].value);
      if (optionIndex == -1) {
        currentFacet.options.push(facet.options[0]);
      } else {
        const isOptionSelected = currentFacetOptions[optionIndex]?.selected;
        currentFacet.options[optionIndex] = {...currentFacetOptions[optionIndex], selected: !isOptionSelected}
      }
    }

    this.notifyFacetUpdate(this.facets);
    this.pageNumber = 0;
    let result:Result = await this.getResult();
    this.notifySubscribers(result);
    return result;
  }

  public getFacets(): IFacet[] {
    return this.facets;
  }

  public async setPage(pageNumber: number): Promise<Result> {
    this.pageNumber = pageNumber;
    let result:Result = await this.getResult();
    this.notifySubscribers(result);
    return result;
  }

  public destroy():void {
    super.destroy();
  }

  setQuery(query: string): void {
    this.query = query;
    this.notifySearchTextUpdate(query);
  }
}
